/**
 * Calendar helpers working on 'YYYY-MM-DD' strings. Dates are calendar days
 * at the venue, so we do the arithmetic in UTC to avoid the browser's own
 * timezone (and DST) shifting them.
 */

const DAY = 86_400_000;

export function parseDate(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

export function toDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  return toDate(new Date(parseDate(date).getTime() + days * DAY));
}

export function diffDays(a: string, b: string): number {
  return Math.round((parseDate(a).getTime() - parseDate(b).getTime()) / DAY);
}

/** Today's date at a venue (IANA timezone). */
export function todayIn(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Current time (HH:MM) at a venue. */
export function nowTimeIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
}

const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', ...opts });
const longFmt = fmt({ weekday: 'long', day: 'numeric', month: 'long' });
const longYearFmt = fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const shortFmt = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
const dayFmt = fmt({ weekday: 'short' });
const monthFmt = fmt({ month: 'long', year: 'numeric' });

/** "Friday 9 October" */
export function longDate(date: string, withYear = false): string {
  return (withYear ? longYearFmt : longFmt).format(parseDate(date)).replace(',', '');
}

/** "Fri 9 Oct" */
export function shortDate(date: string): string {
  return shortFmt.format(parseDate(date)).replace(',', '');
}

export function weekdayShort(date: string): string {
  return dayFmt.format(parseDate(date));
}

export function dayOfMonth(date: string): number {
  return parseDate(date).getUTCDate();
}

export function monthTitle(date: string): string {
  return monthFmt.format(parseDate(date));
}

/** "Today", "Tomorrow", "Fri 9 Oct" */
export function relativeDay(date: string, today: string): string {
  const d = diffDays(date, today);
  if (d === 0) return 'Today';
  if (d === 1) return 'Tomorrow';
  if (d === -1) return 'Yesterday';
  return shortDate(date);
}

/** ISO weekday 1 (Mon) - 7 (Sun). */
export function isoWeekday(date: string): number {
  const d = parseDate(date).getUTCDay();
  return d === 0 ? 7 : d;
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/** 6x7 grid of dates for a month view starting on Monday. */
export function monthGrid(month: string): string[] {
  const first = startOfMonth(month);
  const start = addDays(first, -(isoWeekday(first) - 1));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function addMonths(date: string, months: number): string {
  const d = parseDate(startOfMonth(date));
  d.setUTCMonth(d.getUTCMonth() + months);
  return toDate(d);
}

/** "19:30" -> "7:30pm" */
export function time12(time: string): string {
  const [h, m] = time.split(':').map(Number) as [number, number];
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

export function minutes(time: string): number {
  const [h, m] = time.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

export function fromMinutes(total: number): string {
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Relative time for timestamps: "3 days ago", "in 2 hours". */
export function timeAgo(iso: string, now = Date.now()): string {
  const diff = new Date(iso).getTime() - now;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 365 * DAY],
    ['month', 30 * DAY],
    ['week', 7 * DAY],
    ['day', DAY],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];
  for (const [unit, ms] of units) if (abs >= ms) return rtf.format(Math.round(diff / ms), unit);
  return 'just now';
}

export function formatDateTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: timezone, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}
